import { autobind } from 'core-decorators';
import React, { Component } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ArticleAffiliationsMiddleware from 'app/blocks/article-affiliations-editor/middleware';
import { Layout } from 'app/blocks/blocks';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import { markAsChanged, markAsUnchanged, processing, tryCatch } from 'app/blocks/common/decorators';
import PageEnum from 'app/blocks/common/PageEnum';
import { scrollToTop } from 'app/blocks/common/scroll';
import { cloneDeepSimple, compose, Handlers } from 'app/blocks/common/utils';
import withConfirmLeaving from 'app/blocks/common/withConfirmLeaving';
import * as middlewareLicense from 'app/blocks/middleware/license';
import * as Middleware from 'app/blocks/middleware/middleware';
import * as middlewareProfile from 'app/blocks/middleware/profile';
import { StatusPopupTypeEnum } from 'app/blocks/StatusPopup/StatusPopup';
import AffiliationsStepEnum from './AffiliationsStepEnum';
import ArticleAffiliationsEditorView from './article-affiliations-editor-view';
import { PriceChangeType } from './types';
import {
    confirmAffiliationDeletion,
    evaluatePriceChangeDecision,
    isEmailSuggestion,
    prepareAffiliationsForEditor,
} from './utils';

const HIDE_POPUP_TIMEOUT = 10000;

function mapPickerItemToInstitution(val) {
    return {
        ...val,
        custom: val.custom || false,
        institutionId: val.id || val.institutionId,
        institutionName: val.name || val.institutionName,
    };
}

type Props = Pick<GetProps<typeof ArticleAffiliationsEditorView>, 'renderButtons' | 'zeroPriceOrderExists'> & {
    afterCancelHandler?: (affiliation: ArticleAffiliation[]) => void;
    afterSaveHandler?: (affiliation: ArticleAffiliation[], country: string) => void;
    article: Product.Article;
    articleAffiliations: any;
    articleId: string;
    disabled?: boolean;
    isAuthorPaid?: boolean;
    journal: Product.Journal;
    l: l;
    setChangesChecker: (fn: () => boolean) => void;
    onConfirmSubstep: (isStepConfirmed: boolean) => void;
    onLoadSubstep: (subStep?: string, page?: string, substepData?: any) => void;
    onStateChange?: (
        subStep: Values<typeof AffiliationsStepEnum>,
        priceChangeType: PriceChangeType,
        rejectionReasonAccountBalanceNotEnough?: boolean,
    ) => void;
    updateContext: () => void;
    countryName: string;
    countryDropdownVisible: boolean;
};

type InheritState = Pick<
    GetProps<typeof ArticleAffiliationsEditorView>,
    | 'affiliations'
    | 'article'
    | 'geoTargetedMessage'
    | 'institutionsIdFromProfile'
    | 'isSaving'
    | 'journal'
    | 'newAffiliation'
    | 'noAffiliations'
    | 'noGeoWaiver'
    | 'selectedPrimaryAffiliationId'
    | 'statusPopup'
>;

type State = InheritState & {
    confirmed: boolean;
    emailSuggestionsResolved: any[];
    hasPreviouslyConfirmedPriceChange: boolean;
    eligibleAffiliationsAtLastConfirmation: any[];
    initialAffiliations: any[];
    isLoading: boolean;
    isLoadingError?: Error;
    needWoaConfirmation: boolean;
    notAffiliated: boolean;
    selectedAffiliationByCountry?: Object;
    isPriceProposalCountry: boolean;
    subStep: Values<typeof AffiliationsStepEnum>;
    countryName: string;
    initialCountryName: string;
    priceChangeType?: PriceChangeType;
    latestChangeByUser?: boolean;
    rejectionReasonAccountBalanceNotEnough?: boolean;
    rejectionReasonAccountBalanceNotEnoughInstitutionName?: string;
};

const pages = {
    [AffiliationsStepEnum.SELECT_PRIMARY_AFFILIATION]: PageEnum.AFFILIATIONS_SELECT_PRIMARY,
    [AffiliationsStepEnum.SELECT_AFFILIATION_BY_COUNTRY]: PageEnum.AFFILIATIONS_SELECT_WOA_INSTITUTION,
    [AffiliationsStepEnum.CONFIRM_PRICE_CHANGE]: PageEnum.AFFILIATIONS_DISPLAY_PRICE_CHANGE,
    [AffiliationsStepEnum.CONFIRM_COUNTRY]: PageEnum.AFFILIATIONS_CONFIRM_COUNTRY_CHANGE,
};

const handlers = new Handlers();

@autobind
class ArticleAffiliationsEditor extends Component<Props, State> {
    // eslint-disable-next-line react/no-unused-class-component-methods
    // @ts-ignore
    // eslint-disable-next-line react/no-unused-class-component-methods
    _ = handlers.link(this, 'onLoad');

    static defaultProps = {
        afterCancelHandler: undefined,
        afterSaveHandler: undefined,
        disabled: false,
        isAuthorPaid: false,
        onStateChange: undefined,
    };

    static normalize(affiliations) {
        return affiliations.map(({ confirmed = false, custom = false, ...x }) => ({ ...x, confirmed, custom }));
    }

    state: State = {
        affiliations: [],
        article: null,
        confirmed: false,
        countryName: this.props.countryName,
        eligibleAffiliationsAtLastConfirmation: [],
        emailSuggestionsResolved: [],
        hasPreviouslyConfirmedPriceChange: false,
        initialAffiliations: [],
        initialCountryName: this.props.countryName, // NEW: Track initial country for comparison
        institutionsIdFromProfile: [],
        isLoading: true,
        isPriceProposalCountry: true,
        isSaving: false,
        journal: null,
        latestChangeByUser: false,
        needWoaConfirmation: false,
        newAffiliation: false,
        noAffiliations: false,
        noGeoWaiver: false,
        notAffiliated: false,
        subStep: AffiliationsStepEnum.AFFILIATION_LIST,
    };

    isChanged = false;

    popupTimer;

    // see https://jira.wiley.com/browse/AS-15471 for this strange logic details - it prevents 'blinking'
    // flag 'hideEligibilityMessage' is used in ./article-affiliations-panel/Institutions/ConfirmedInstitution/ConfirmedInstitution.tsx
    hideNewEligibility(affiliations = []) {
        const { initialAffiliations = [] } = this.state;

        return affiliations.map(newAffiliation => {
            if (newAffiliation.eligibleForOO) {
                const oldAffiliation = initialAffiliations.find(
                    old => old.affiliationId === newAffiliation.affiliationId,
                );
                if (!(oldAffiliation && oldAffiliation.eligibleForOO)) {
                    return { ...newAffiliation, hideEligibilityMessage: true };
                }
            }
            return newAffiliation;
        });
    }

    getIndex(id) {
        return this.state.affiliations.findIndex(aff => aff.id === id);
    }

    isDisabled() {
        return this.state.isLoading || this.state.isSaving || this.props.disabled;
    }

    isEmptyAffiliations() {
        return this.state.affiliations.length === 0;
    }

    onPriceProposalCountryChange(value: boolean) {
        this.setState({ isPriceProposalCountry: value });
    }

    onCountryChange(basedCountryName) {
        this.setState({ countryName: basedCountryName });
    }

    canConfirm() {
        if (this.state.subStep === AffiliationsStepEnum.SELECT_AFFILIATION_BY_COUNTRY) {
            return this.state.selectedAffiliationByCountry !== null || this.state.notAffiliated;
        }
        if (this.state.subStep === AffiliationsStepEnum.SELECT_PRIMARY_AFFILIATION) {
            return !!this.state.selectedPrimaryAffiliationId;
        }
        if (this.state.subStep === AffiliationsStepEnum.CONFIRM_PRICE_CHANGE) {
            return !this.isDisabled();
        }

        const unconfirmedAffiliations = (this.state.affiliations || []).filter(x => !x.confirmed);

        let isFilled: boolean = false;

        if (this.state.journal.revenueModel === 'OA' && this.props.countryDropdownVisible) {
            isFilled =
                (this.state.noAffiliations || !this.isEmptyAffiliations()) &&
                (this.state.noGeoWaiver || this.state.isPriceProposalCountry);
            return !this.isDisabled() && isFilled && unconfirmedAffiliations.length === 0 && !!this.state.countryName;
        }
        isFilled = this.state.noAffiliations || !this.isEmptyAffiliations();
        return !this.isDisabled() && isFilled && unconfirmedAffiliations.length === 0;
    }

    get isWaiverAvailable() {
        return !!this.props.articleAffiliations?.waiverType;
    }

    hasCountryChanged() {
        return (
            this.state.initialCountryName &&
            this.state.countryName &&
            this.state.initialCountryName !== this.state.countryName
        );
    }

    shouldShowCountryChangeWarning() {
        const { articleAffiliations } = this.props;
        return (
            this.hasCountryChanged() &&
            articleAffiliations?.discountType === 'GeographicalDiscount' &&
            articleAffiliations?.percentageDiscount !== undefined &&
            articleAffiliations?.percentageDiscount !== null
        );
    }

    @autobind
    @tryCatch.showPopUpAndGoToDashboard
    onEditAffiliation() {
        this.setState(prevState => ({
            countryName: prevState.initialCountryName,
            subStep: AffiliationsStepEnum.AFFILIATION_LIST,
        }));
    }

    @autobind
    async onConfirmCountryChange() {
        // Simply proceed with the flow - the substep will be automatically handled
        // We need to manually call the next steps since affiliations are already saved
        const { afterSaveHandler, articleAffiliations, articleId, isAuthorPaid, journal, onConfirmSubstep } =
            this.props;
        const { affiliations, countryName } = this.state;

        // Get the price change decision without the country check
        const selectedPrimaryAffiliation = affiliations.filter(i => i.primary);
        const primaryAffiliationId = selectedPrimaryAffiliation.length
            ? selectedPrimaryAffiliation[0].affiliationId
            : null;

        const { eligibleAffiliations, priceChangeType, shouldShowPriceChangeConfirmation } =
            evaluatePriceChangeDecision({
                affiliations,
                affiliationsConfirmed: false,
                fundingGroupId: articleAffiliations?.fundingInstitutionGroupId,
                isAuthorPaid,
                isWaiverAvailable: this.isWaiverAvailable,
                journalRevenueModel: journal.revenueModel,
                selectedPrimaryAffiliationId: primaryAffiliationId,
            });

        // Check what the next step should be
        if (eligibleAffiliations.length > 1) {
            onConfirmSubstep(false);
            this.setState({
                priceChangeType,
                selectedPrimaryAffiliationId: primaryAffiliationId,
                subStep: AffiliationsStepEnum.SELECT_PRIMARY_AFFILIATION,
            });
        } else if (shouldShowPriceChangeConfirmation) {
            onConfirmSubstep(false);
            this.setState({
                priceChangeType,
                selectedPrimaryAffiliationId: eligibleAffiliations[0]?.affiliationId || null,
                subStep: AffiliationsStepEnum.CONFIRM_PRICE_CHANGE,
            });
        } else if (afterSaveHandler) {
            await ArticleAffiliationsMiddleware.confirmAffiliations(articleId);
            afterSaveHandler(affiliations, countryName);
        } else {
            onConfirmSubstep(true);
        }
    }

    getNormalizedAffilitaions(affiliations) {
        return ArticleAffiliationsEditor.normalize(this.removeEmailSuggestions(affiliations || []));
    }

    getBlackList = affiliations => (affiliations || []).filter(x => !!x.institutionId).map(x => x.institutionId);

    // eslint-disable-next-line react/no-unused-class-component-methods
    onLoad(error?) {
        const { priceChangeType, rejectionReasonAccountBalanceNotEnough, subStep } = this.state;
        const { onLoadSubstep, onStateChange } = this.props;

        if (!!subStep && typeof onLoadSubstep === 'function') {
            const page = pages[subStep] ?? PageEnum.AFFILIATIONS;
            const substepData = {
                priceChangeType,
                // Only include rejection reasons if priceChangeType exists
                ...(priceChangeType && {
                    rejectionReasonAccountBalanceNotEnough,
                }),
            };
            onLoadSubstep(subStep, page, substepData);
        }

        if (onStateChange) {
            onStateChange(subStep, priceChangeType);
        }

        if (error) {
            throw error;
        }
    }

    @markAsUnchanged
    @processing('isLoading')
    @tryCatch.showPopUpAndGoToDashboard
    // @ts-ignore
    @tryCatch({ errorHandler: e => handlers.onLoad(e) })
    async reload() {
        const promiseResults = await Promise.all([
            this.loadArticleAndJournal(),
            this.loadAffiliations(),
            middlewareProfile.getAffiliations(),
        ]);

        const [
            { article, journal },
            { affiliations, geoTargetedMessage, latestChangeByUser, needWoaConfirmation, suggestions },
            profileAffiliations,
        ] = promiseResults;

        const preparedAffiliations = prepareAffiliationsForEditor(affiliations, suggestions, latestChangeByUser);

        this.setState(prevState => ({
            affiliations: preparedAffiliations,
            article,
            countryName: this.props.articleAffiliations?.countryName, // NEW: Set current country
            eligibleAffiliationsAtLastConfirmation: prevState.eligibleAffiliationsAtLastConfirmation,
            geoTargetedMessage: !latestChangeByUser && geoTargetedMessage ? geoTargetedMessage.html : null,
            hasPreviouslyConfirmedPriceChange: prevState.hasPreviouslyConfirmedPriceChange,
            initialAffiliations: cloneDeepSimple(preparedAffiliations),
            initialCountryName: this.props.articleAffiliations?.countryName, // NEW: Store initial country
            institutionsIdFromProfile: profileAffiliations.map(aff => aff.institutionId).filter(instId => instId),
            journal,
            latestChangeByUser,
            needWoaConfirmation,
            noAffiliations: latestChangeByUser && affiliations.length === 0,
        }));

        this.props.setChangesChecker(() => this.isChanged);
        this.onLoad();
    }

    componentDidMount() {
        this.reload();
    }

    componentDidUpdate(prevProps, prevState) {
        const { priceChangeType, subStep } = this.state;
        const { onStateChange } = this.props;

        if (subStep !== prevState.subStep) {
            this.onLoad();
            this.scrollToTopAfterRender();
        }

        if (
            onStateChange &&
            (subStep !== prevState.subStep ||
                priceChangeType !== prevState.priceChangeType ||
                this.state.rejectionReasonAccountBalanceNotEnough !==
                    prevState.rejectionReasonAccountBalanceNotEnough ||
                this.state.rejectionReasonAccountBalanceNotEnoughInstitutionName !==
                    prevState.rejectionReasonAccountBalanceNotEnoughInstitutionName)
        ) {
            onStateChange(subStep, this.state.priceChangeType, this.state.rejectionReasonAccountBalanceNotEnough);
        }
    }

    scrollToTopAfterRender = () => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scrollToTop();
            });
        });
    };

    componentWillUnmount() {
        clearTimeout(this.popupTimer);
    }

    loadArticleAndJournal = () => {
        // TODO fix after standalone affiliation pages are removed
        const { article, articleId, journal } = this.props;
        if (article && journal) {
            return Promise.resolve({ article, journal });
        }

        return Middleware.product.getArticleAndJournal(articleId);
    };

    loadAffiliations() {
        return ArticleAffiliationsMiddleware.getAffiliations(this.props.articleId);
    }

    removeEmailSuggestions(affiliations) {
        return affiliations.filter(aff => !aff.emailMatched);
    }

    async saveAffiliations(andCheck: boolean = false) {
        const { emailSuggestionsResolved } = this.state;

        const { affiliations, needWoaConfirmation, suggestions } = await ArticleAffiliationsMiddleware.saveAffiliations(
            this.props.articleId,
            {
                affiliations: this.getNormalizedAffilitaions(this.state.affiliations),
                emailSuggestionsResolved,
            },
        );

        if (this.state.journal?.revenueModel === 'OO') {
            await middlewareLicense.getBasePrices(this.props.articleId);
        }
        await this.props.updateContext();

        await new Promise<void>(resolve =>
            this.setState(
                prevState => ({
                    affiliations: this.hideNewEligibility(
                        prepareAffiliationsForEditor(affiliations, suggestions, true),
                    ),
                    eligibleAffiliationsAtLastConfirmation: prevState.eligibleAffiliationsAtLastConfirmation,
                    needWoaConfirmation,
                }),
                () => resolve(),
            ),
        );
        if (andCheck) {
            await this.checkAffiliationList();
        } else {
            this.props.onConfirmSubstep(false);
        }
    }

    async savePrimaryAffiliation() {
        await ArticleAffiliationsMiddleware.savePrimaryAffiliation(
            this.props.articleId,
            this.state.selectedPrimaryAffiliationId,
        );
    }

    checkAffiliationList = async () => {
        const { afterSaveHandler, articleAffiliations, articleId, isAuthorPaid, journal, onConfirmSubstep } =
            this.props;
        const { affiliations, countryName, latestChangeByUser, needWoaConfirmation, subStep } = this.state;

        // Check if we need to show country change warning before proceeding
        if (subStep !== AffiliationsStepEnum.CONFIRM_COUNTRY && this.shouldShowCountryChangeWarning()) {
            onConfirmSubstep(false);

            const percentageDiscount = articleAffiliations?.percentageDiscount;
            const specificPriceChangeType =
                percentageDiscount === 100
                    ? PriceChangeType.AUTHOR_GEOGRAPHIC_WAIVER_TO_AUTHOR
                    : PriceChangeType.AUTHOR_GEOGRAPHIC_DISCOUNT_TO_AUTHOR;

            this.setState({
                priceChangeType: specificPriceChangeType,
                subStep: AffiliationsStepEnum.CONFIRM_COUNTRY,
            });
            return;
        }

        const selectedPrimaryAffiliation = affiliations.filter(i => i.primary);
        const selectedPrimaryAffiliationId = selectedPrimaryAffiliation.length
            ? selectedPrimaryAffiliation[0].affiliationId
            : null;

        // Extract rejectedAccounts from all affiliations
        const rejectedAccounts = affiliations
            .flatMap(aff => (aff as any).rejectedAccounts || [])
            .filter(account => account && account.code);

        const woaCode = articleAffiliations?.woaCode;

        const {
            eligibleAffiliations,
            priceChangeType,
            rejectionReasonAccountBalanceNotEnough,
            rejectionReasonAccountBalanceNotEnoughInstitutionName,
            shouldShowPriceChangeConfirmation,
        } = evaluatePriceChangeDecision({
            affiliations,
            affiliationsConfirmed: articleAffiliations?.confirmed && !latestChangeByUser,
            fundingGroupId: articleAffiliations?.fundingInstitutionGroupId,
            isAuthorPaid,
            isWaiverAvailable: this.isWaiverAvailable,
            journalRevenueModel: journal.revenueModel,
            rejectedAccounts,
            selectedPrimaryAffiliationId,
            woaCode,
        });

        if (needWoaConfirmation) {
            onConfirmSubstep(false);
            this.setState({
                notAffiliated: false,
                selectedAffiliationByCountry: null,
                subStep: AffiliationsStepEnum.SELECT_AFFILIATION_BY_COUNTRY,
            });
        } else if (eligibleAffiliations.length > 1) {
            onConfirmSubstep(false);

            this.setState({
                priceChangeType,
                selectedPrimaryAffiliationId,
                subStep: AffiliationsStepEnum.SELECT_PRIMARY_AFFILIATION,
            });
        } else if (shouldShowPriceChangeConfirmation) {
            onConfirmSubstep(false);
            this.setState({
                priceChangeType,
                rejectionReasonAccountBalanceNotEnough,
                rejectionReasonAccountBalanceNotEnoughInstitutionName,
                selectedPrimaryAffiliationId: eligibleAffiliations[0]?.affiliationId || null,
                subStep: AffiliationsStepEnum.CONFIRM_PRICE_CHANGE,
            });
        } else if (afterSaveHandler) {
            await ArticleAffiliationsMiddleware.confirmAffiliations(articleId);
            afterSaveHandler(affiliations, countryName);
        } else {
            onConfirmSubstep(false);
        }
    };

    async onClickSavePrimaryAffiliation() {
        const { afterSaveHandler, articleAffiliations, isAuthorPaid, journal } = this.props;
        const { affiliations, countryName, selectedPrimaryAffiliationId } = this.state;

        await this.savePrimaryAffiliation();

        // Extract rejectedAccounts from all affiliations
        const rejectedAccounts = affiliations
            .flatMap(aff => (aff as any).rejectedAccounts || [])
            .filter(account => account && account.code);

        const woaCode = articleAffiliations?.woaCode;

        const {
            priceChangeType,
            rejectionReasonAccountBalanceNotEnough,
            rejectionReasonAccountBalanceNotEnoughInstitutionName,
            shouldShowPriceChangeConfirmation,
        } = evaluatePriceChangeDecision({
            affiliations,
            affiliationsConfirmed: false,
            fundingGroupId: articleAffiliations?.fundingInstitutionGroupId,
            isAuthorPaid,
            isWaiverAvailable: this.isWaiverAvailable,
            journalRevenueModel: journal.revenueModel,
            rejectedAccounts,
            selectedPrimaryAffiliationId,
            woaCode,
        });

        if (shouldShowPriceChangeConfirmation) {
            this.setState({
                priceChangeType,
                rejectionReasonAccountBalanceNotEnough,
                rejectionReasonAccountBalanceNotEnoughInstitutionName,
                subStep: AffiliationsStepEnum.CONFIRM_PRICE_CHANGE,
            });
        } else if (afterSaveHandler) {
            await ArticleAffiliationsMiddleware.confirmAffiliations(this.props.articleId);
            afterSaveHandler(affiliations, countryName);
        } else {
            this.props.onConfirmSubstep(true);
        }
    }

    async onClickSaveWOAInstitution() {
        const { affiliations, countryName, selectedAffiliationByCountry: institution } = this.state;
        const { afterSaveHandler, articleId } = this.props;

        if (institution) {
            const affiliation = mapPickerItemToInstitution(institution);
            affiliation.confirmed = true;

            affiliations.push(affiliation);
            await new Promise<void>(resolve => this.setState({ affiliations: [...affiliations] }, () => resolve()));

            await this.saveAffiliations();
            await ArticleAffiliationsMiddleware.confirmAffiliations(articleId);

            this.setState({ subStep: AffiliationsStepEnum.AFFILIATION_LIST });
            this.props.onConfirmSubstep(false);
        } else {
            await ArticleAffiliationsMiddleware.confirmAffiliations(articleId);
            afterSaveHandler(affiliations, countryName);
        }
    }

    async onClickConfirmPriceChange(priceChangeType?: PriceChangeType) {
        const { afterSaveHandler, articleId } = this.props;

        await new Promise<void>(resolve =>
            this.setState(
                prevState => ({
                    eligibleAffiliationsAtLastConfirmation: [...prevState.affiliations],
                    hasPreviouslyConfirmedPriceChange: true,
                }),
                () => {
                    resolve();
                },
            ),
        );

        if ([PriceChangeType.AUTHOR_TO_FUNDER, PriceChangeType.FUNDER_TO_FUNDER].includes(priceChangeType)) {
            await this.savePrimaryAffiliation();
        }

        await this.saveAffiliations();
        await ArticleAffiliationsMiddleware.confirmAffiliations(articleId);

        if (afterSaveHandler) {
            afterSaveHandler(this.state.affiliations, this.state.countryName);
        } else {
            this.props.onConfirmSubstep(true);
            this.setState({
                priceChangeType: undefined,
            });
        }
    }

    @markAsChanged
    onSelectAffiliationAsPrimary(selectedPrimaryAffiliationId) {
        this.setState({ selectedPrimaryAffiliationId });
    }

    @markAsChanged
    onSelectAffiliationByCountry(institution) {
        this.setState({ selectedAffiliationByCountry: institution });
    }

    @markAsUnchanged
    @processing('isSaving')
    @tryCatch.popUpOnly
    async onClickSave() {
        if (this.state.subStep === AffiliationsStepEnum.AFFILIATION_LIST) {
            await this.saveAffiliations(true);
        } else if (this.state.subStep === AffiliationsStepEnum.SELECT_AFFILIATION_BY_COUNTRY) {
            await this.onClickSaveWOAInstitution();
        } else if (this.state.subStep === AffiliationsStepEnum.CONFIRM_PRICE_CHANGE) {
            await this.onClickConfirmPriceChange(this.state.priceChangeType);
        } else if (this.state.subStep === AffiliationsStepEnum.CONFIRM_COUNTRY) {
            await this.onConfirmCountryChange();
        } else {
            await this.onClickSavePrimaryAffiliation();
        }
    }

    async onClickCancel() {
        const { afterCancelHandler } = this.props;
        const { affiliations, subStep } = this.state;
        if (subStep === AffiliationsStepEnum.AFFILIATION_LIST && afterCancelHandler) {
            afterCancelHandler(affiliations);
        } else if (subStep === AffiliationsStepEnum.SELECT_AFFILIATION_BY_COUNTRY) {
            this.setState({
                subStep: AffiliationsStepEnum.AFFILIATION_LIST,
            });
        } else if (subStep === AffiliationsStepEnum.CONFIRM_COUNTRY) {
            this.onEditAffiliation();
        } else {
            this.setState({ subStep: AffiliationsStepEnum.AFFILIATION_LIST });
            this.reload();
        }
    }

    @markAsChanged
    onNoAffiliationsChange() {
        const { noAffiliations } = this.state;
        const willBeNo = !noAffiliations;

        if (willBeNo) {
            this.setState({ newAffiliation: false });
        }

        this.setState({ noAffiliations: this.isEmptyAffiliations() && willBeNo });
    }

    onNoGeoWaiverChange = () => {
        const { noGeoWaiver } = this.state;
        this.setState({ noGeoWaiver: !noGeoWaiver });
    };

    // this will set 'confirmed', NOT set affiliationId, and 'possibleMatches' will be removed as well
    @markAsChanged
    onChange(id, institution) {
        const affiliation = mapPickerItemToInstitution(institution);
        const { affiliations, confirmed, emailSuggestionsResolved } = this.state;

        let affIndex = this.getIndex(id);
        if (!id) {
            affIndex = affiliations.push(affiliation) - 1;
            setTimeout(() => {
                this.setState({ newAffiliation: false });
            }, 0);
        }

        const prev = affiliations[affIndex];

        affiliation.id = prev.id || uuidv4();
        affiliation.confirmed = true;

        if (affiliation.emailMatched) {
            emailSuggestionsResolved.push(affiliation.emailMatched);
            this.setState({ emailSuggestionsResolved });
            delete affiliation.emailMatched;
        }
        if (id && !prev.confirmed) {
            this.showPopup(
                StatusPopupTypeEnum.INFO,
                this.props.l('ARTICLE_AFFILIATION_EDITOR.POPUP_UPDATE_INFO', {
                    from: prev.institutionName,
                    name: affiliation.institutionName,
                }),
            );
        }

        affiliations[affIndex] = affiliation;

        this.setState({
            affiliations: [...affiliations],
            confirmed,
        });
    }

    @markAsChanged
    async onDelete(id) {
        const { l } = this.props;
        const { affiliations, emailSuggestionsResolved } = this.state;

        const affIndex = this.getIndex(id);
        const affiliation = affiliations[affIndex];
        const name = isEmailSuggestion(affiliation) ? affiliation.name : affiliation.institutionName;
        const emailMatched = !!affiliation.emailMatched;

        const onApprove = () => {
            this.showPopup(StatusPopupTypeEnum.WARN, l('ARTICLE_AFFILIATION_EDITOR.POPUP_DELETE_INFO', { name }));

            this.setState({ affiliations: affiliations.filter(aff => aff.id !== id) });
        };

        if (emailMatched) {
            emailSuggestionsResolved.push(affiliation.emailMatched);
            this.setState({ emailSuggestionsResolved }, onApprove);
        } else if (await confirmAffiliationDeletion(name, affiliation.eligibleForOO)) {
            onApprove();
        }
    }

    @markAsChanged
    onAdd() {
        this.setState({ newAffiliation: true });
    }

    onCancel = () => {
        this.setState({ newAffiliation: false });
    };

    showPopup(type, text) {
        // eslint-disable-next-line sort-keys
        this.setState({ statusPopup: { text, type } });

        clearTimeout(this.popupTimer);
        this.popupTimer = setTimeout(() => this.onCloseStatusPopup(), HIDE_POPUP_TIMEOUT);
    }

    onCloseStatusPopup = () => {
        clearTimeout(this.popupTimer);
        this.setState({ statusPopup: null });
    };

    @markAsChanged
    setNotAffiliated(notAffiliated) {
        this.setState({ notAffiliated });
    }

    render() {
        const { articleAffiliations, countryDropdownVisible, renderButtons, zeroPriceOrderExists } = this.props;
        const {
            affiliations,
            article,
            countryName,
            geoTargetedMessage,
            initialCountryName,
            institutionsIdFromProfile,
            isLoading,
            isLoadingError,
            isSaving,
            journal,
            newAffiliation,
            noAffiliations,
            noGeoWaiver,
            selectedPrimaryAffiliationId,
            statusPopup,
            subStep,
        } = this.state;

        return (
            <Layout isLoading={isLoading}>
                {!isLoading && !isLoadingError && (
                    <ArticleAffiliationsEditorView
                        affiliations={affiliations}
                        article={article}
                        articleAffiliations={articleAffiliations}
                        blacklist={this.getBlackList(affiliations)}
                        canCancel={!this.isDisabled()}
                        canConfirm={this.canConfirm()}
                        countryDropdownVisible={countryDropdownVisible}
                        countryName={countryName}
                        eligibleAffiliations={affiliations.filter(i => i.eligibleForOO)}
                        geoTargetedMessage={geoTargetedMessage}
                        initialCountryName={initialCountryName}
                        institutionsIdFromProfile={institutionsIdFromProfile}
                        isDisabled={this.isDisabled()}
                        isEmptyAffiliations={this.isEmptyAffiliations()}
                        isSaving={isSaving}
                        journal={journal}
                        newAffiliation={newAffiliation}
                        noAffiliations={noAffiliations}
                        noGeoWaiver={noGeoWaiver}
                        onAdd={this.onAdd}
                        onCancel={this.onCancel}
                        onChange={this.onChange}
                        onClickCancel={this.onClickCancel}
                        onClickSave={this.onClickSave}
                        onCloseStatusPopup={this.onCloseStatusPopup}
                        onCountryChange={this.onCountryChange}
                        onDelete={this.onDelete}
                        onNoAffiliationsChange={this.onNoAffiliationsChange}
                        onNoGeoWaiverChange={this.onNoGeoWaiverChange}
                        onPriceProposalCountryChange={this.onPriceProposalCountryChange}
                        onSelectAffiliationAsPrimary={this.onSelectAffiliationAsPrimary}
                        onSelectAffiliationByCountry={this.onSelectAffiliationByCountry}
                        priceChangeType={this.state.priceChangeType}
                        rejectionReasonAccountBalanceNotEnough={this.state.rejectionReasonAccountBalanceNotEnough}
                        rejectionReasonAccountBalanceNotEnoughInstitutionName={
                            this.state.rejectionReasonAccountBalanceNotEnoughInstitutionName
                        }
                        renderButtons={renderButtons}
                        selectedPrimaryAffiliationId={selectedPrimaryAffiliationId}
                        setNotAffiliated={this.setNotAffiliated}
                        statusPopup={statusPopup}
                        subStep={subStep}
                        zeroPriceOrderExists={zeroPriceOrderExists}
                    />
                )}
            </Layout>
        );
    }
}

export default compose(
    withConfirmLeaving,
    withCodes2(ID.BUTTONS, ID.ARTICLE_AFFILIATION_EDITOR),
)(ArticleAffiliationsEditor);
